/* eslint-disable no-underscore-dangle */
import React, { memo, useMemo } from 'react';
import { Link } from 'react-router';
import classNames from 'classnames';

import { BASE_URL } from '@/shared/config/env';
import { joinBaseUrl } from '@/shared/utils/url';

import Bevel from '@/shared/components/button/Bevel';
import { ButtonProps } from '@/shared/components/button/types';
import Spinner from '@/shared/components/spinner/Spinner';

import type { Ref } from 'react';

const Button = ({
	active,
	additionalSubTitle,
	avaName,
	avaOutside,
	btnType = 'submit',
	buttonRef,
	children,
	className,
	color,
	correctAnswer = null,
	description,
	disabled,
	disabledDropdownItem,
	download,
	href,
	iconClassName = 'icon',
	iconName,
	imgSrc,
	id,
	label,
	labelColor,
	link,
	loading,
	name = '',
	onClick,
	rounded,
	small,
	subTitle,
	symbol,
	tabIndex = -1,
	title,
	type = 'primary',
	style,
	testId,
}: ButtonProps) => {
	const hasBevel = useMemo(() => {
		return ['primary', 'secondary'].includes(type) && !disabled;
	}, [type, disabled]);

	const bevelColor = useMemo(() => {
		if (type === 'primary' && !color) {
			return 'yellow';
		}
		if (type === 'secondary' && !color) {
			return 'sand';
		}
		return color;
	}, [type, color]);

	const _className = classNames(
		type ? `btn-${type}` : null,
		{
			disabled,
			loading,
			small,
			rounded,
			active: active || loading,
			grey: color === 'grey',
			green: color === 'green',
			white: color === 'white',
			red: color === 'red',
			blue: color === 'blue',
			purple: color === 'purple',
			correct: correctAnswer,
			incorrect: correctAnswer === false,
			'disabled-dropdown-item': disabledDropdownItem,
		},
		!hasBevel && className,
		description ? 'sub-desc' : null,
	);

	const handleClickButton = (e: any) => {
		if (disabled || loading || disabledDropdownItem) {
			return;
		}
		onClick && onClick(e);
	};

	const VisualWrapper = () => (
		<>
			{['primary', 'secondary'].indexOf(type) >= 0 ? (
				<div className="visual">
					<Content />
				</div>
			) : (
				<Content />
			)}
		</>
	);

	const Content = () => (
		<>
			{avaName && !symbol && (
				<div className="avatar">
					<img
						src={avaOutside ? avaName : joinBaseUrl(BASE_URL, avaName)}
						alt="subjectLogo"
						width="24px"
						height="24px"
					/>
				</div>
			)}
			{iconName && !symbol && !avaName && !loading && <span className={classNames(iconClassName, iconName)} />}
			{imgSrc && <img className="image" src={imgSrc} alt="img" />}
			{title && !subTitle && !description && <span className="text">{title}</span>}
			{title && description && (
				<div className="desc-block">
					<span className="text">{title}</span>
					<span className="text desc">{description}</span>
				</div>
			)}
			{title && subTitle && !description && (
				<div className="desc-block">
					<span className="text">{title}</span>
					<span className="text sub-text">{subTitle}</span>
					{additionalSubTitle && <span className="text sub-text">{additionalSubTitle}</span>}
				</div>
			)}
			{loading && <Spinner />}
			{symbol && <span className="symbol">{symbol}</span>}
			{label && <span className={classNames('label-wrapper', labelColor)}>{label}</span>}
			{children}
		</>
	);

	const ButtonLink = () =>
		href ? (
			<a
				id={id}
				href={href}
				className={_className}
				rel="noopener noreferrer"
				download={download}
				target="_blank"
				data-testid={testId}
				ref={buttonRef as Ref<HTMLAnchorElement>}
			>
				<VisualWrapper />
			</a>
		) : link ? (
			<Link
				id={id}
				tabIndex={tabIndex}
				to={link}
				className={_className}
				onClick={handleClickButton}
				data-testid={testId}
				style={style}
				ref={buttonRef as Ref<HTMLAnchorElement>}
			>
				<VisualWrapper />
			</Link>
		) : (
			<button
				id={id}
				className={_className}
				onClick={handleClickButton}
				tabIndex={tabIndex}
				type={btnType}
				name={name}
				data-testid={testId}
				style={style}
				ref={buttonRef as Ref<HTMLButtonElement>}
			>
				<VisualWrapper />
			</button>
		);

	if (hasBevel) {
		return (
			<Bevel className={className} color={bevelColor} disabled={disabled} isPressed={active || loading}>
				<ButtonLink />
			</Bevel>
		);
	}

	return <ButtonLink />;
};

export default memo(Button);
